import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes,
  faPlay,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
  faClock,
  faUndo,
  faFileText,
  faShieldAlt,
  faServer,
  faDollarSign
} from '@fortawesome/free-solid-svg-icons';
import { faAws, faGoogle, faGithub } from '@fortawesome/free-brands-svg-icons';
import { 
  Recommendation,
  RecommendationDeployment,
  ExecutedAction,
  DeploymentLog
} from '../types';
import { recommendationService } from '../services/recommendationService';

interface DeployRecommendationModalProps {
  isOpen: boolean;
  onClose: () => void;
  recommendationId: string | null;
  onDeploymentComplete?: (success: boolean) => void;
}

interface DeploymentStep {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  duration?: string;
  error?: string;
  riskLevel?: string;
  rollbackPossible?: boolean;
}

export const DeployRecommendationModal: React.FC<DeployRecommendationModalProps> = ({
  isOpen,
  onClose,
  recommendationId,
  onDeploymentComplete
}) => {
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);
  const [deployment, setDeployment] = useState<RecommendationDeployment | null>(null);
  const [steps, setSteps] = useState<DeploymentStep[]>([]);
  const [logs, setLogs] = useState<DeploymentLog[]>([]);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentStarted, setDeploymentStarted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmationStep, setConfirmationStep] = useState<DeploymentStep | null>(null);
  const [confirmationCallback, setConfirmationCallback] = useState<{
    onConfirm: () => void;
    onSkip: () => void;
    onCancel: () => void;
  } | null>(null);

  // Fetch the actual recommendation from API
  useEffect(() => {
    const fetchRecommendation = async () => {
      if (!isOpen || !recommendationId) return;

      try {
        setLoading(true);
        setError(null);

        console.log('Fetching recommendation:', recommendationId);
        const rec = await recommendationService.getRecommendationById(recommendationId);

        console.log('Recommendation fetched:', rec);
        setRecommendation(rec);

        // Initialize steps from actions if they exist
        if (rec.actions && rec.actions.length > 0) {
          const initialSteps: DeploymentStep[] = rec.actions.map(action => ({
            id: action.id,
            name: action.description,
            status: 'pending',
            duration: action.estimatedDuration,
            riskLevel: action.riskLevel,
            rollbackPossible: action.rollbackPossible
          }));

          setSteps(initialSteps);
        } else {
          // No actions defined - this means deployment needs to be handled differently
          console.warn('Recommendation has no actions defined');
          setError('This recommendation does not have deployment actions configured yet.');
        }

        setLogs([]);
        setDeploymentStarted(false);
        setIsDeploying(false);
      } catch (err: any) {
        console.error('Error fetching recommendation:', err);
        setError(err.message || 'Failed to load recommendation details');
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendation();
  }, [isOpen, recommendationId]);

  // Helper function to show confirmation dialog and wait for user response
  const askUserConfirmation = (step: DeploymentStep): Promise<'confirm' | 'skip' | 'cancel'> => {
    return new Promise((resolve) => {
      setConfirmationStep(step);
      setConfirmationCallback({
        onConfirm: () => {
          setConfirmationStep(null);
          setConfirmationCallback(null);
          resolve('confirm');
        },
        onSkip: () => {
          setConfirmationStep(null);
          setConfirmationCallback(null);
          resolve('skip');
        },
        onCancel: () => {
          setConfirmationStep(null);
          setConfirmationCallback(null);
          resolve('cancel');
        }
      });
    });
  };

  const handleStartDeployment = async () => {
    if (!recommendation) return;

    try {
      setIsDeploying(true);
      setDeploymentStarted(true);
      setError(null);

      // Add initial log entry
      const startLog: DeploymentLog = {
        timestamp: new Date().toISOString(),
        level: 'info',
        message: 'Starting deployment of recommendation...'
      };
      setLogs([startLog]);

      // Execute deployment steps with user confirmation
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];

        // Ask user for confirmation before executing this step
        setLogs(prev => [...prev, {
          timestamp: new Date().toISOString(),
          level: 'info',
          message: `Awaiting user confirmation for: ${step.name}`,
          actionId: step.id
        }]);

        const userChoice = await askUserConfirmation(step);

        if (userChoice === 'cancel') {
          setLogs(prev => [...prev, {
            timestamp: new Date().toISOString(),
            level: 'warning',
            message: 'Deployment cancelled by user'
          }]);
          throw new Error('Deployment cancelled by user');
        }

        if (userChoice === 'skip') {
          // Mark step as skipped
          setSteps(prev => prev.map(s =>
            s.id === step.id ? { ...s, status: 'skipped' } : s
          ));

          setLogs(prev => [...prev, {
            timestamp: new Date().toISOString(),
            level: 'warning',
            message: `Step skipped by user: ${step.name}`,
            actionId: step.id
          }]);

          continue; // Skip to next step
        }

        // User confirmed - execute the step
        setSteps(prev => prev.map(s =>
          s.id === step.id ? { ...s, status: 'running' } : s
        ));

        setLogs(prev => [...prev, {
          timestamp: new Date().toISOString(),
          level: 'info',
          message: `Executing step: ${step.name}`,
          actionId: step.id
        }]);

        try {
          // Call actual backend API to execute the step
          const stepAction = recommendation.actions[i];
          const parameters = {
            instanceIds: [recommendation.resourceDetails.resourceId],
            instanceId: recommendation.resourceDetails.resourceId,
            instanceType: recommendation.resourceDetails.recommendedConfig?.instanceType,
            ...recommendation.resourceDetails
          };

          const result = await recommendationService.executeDeploymentStep(
            recommendation.id,
            step.id,
            stepAction,
            parameters
          );

          if (!result.success) {
            throw new Error(result.error || 'Step execution failed');
          }

          // Mark step as completed
          setSteps(prev => prev.map(s =>
            s.id === step.id ? { ...s, status: 'completed' } : s
          ));

          setLogs(prev => [...prev, {
            timestamp: new Date().toISOString(),
            level: 'info',
            message: `Step completed successfully: ${step.name}${result.result?.message ? ` - ${result.result.message}` : ''}`,
            actionId: step.id
          }]);
        } catch (stepError: any) {
          // Step execution failed
          setSteps(prev => prev.map(s =>
            s.id === step.id ? { ...s, status: 'failed', error: stepError.message } : s
          ));

          setLogs(prev => [...prev, {
            timestamp: new Date().toISOString(),
            level: 'error',
            message: `Step failed: ${step.name} - ${stepError.message}`,
            actionId: step.id
          }]);

          throw stepError;
        }
      }

      // Final success log
      setLogs(prev => [...prev, {
        timestamp: new Date().toISOString(),
        level: 'info',
        message: 'Deployment completed successfully!'
      }]);

      onDeploymentComplete?.(true);

    } catch (err: any) {
      setError(err.message);
      setLogs(prev => [...prev, {
        timestamp: new Date().toISOString(),
        level: 'error',
        message: `Deployment failed: ${err.message}`
      }]);

      onDeploymentComplete?.(false);
    } finally {
      setIsDeploying(false);
    }
  };

  const handleRollback = async () => {
    if (!deployment) return;
    
    try {
      setIsDeploying(true);
      setError(null);
      
      // Simulate rollback
      setLogs(prev => [...prev, {
        timestamp: new Date().toISOString(),
        level: 'info',
        message: 'Starting rollback process...'
      }]);
      
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      setLogs(prev => [...prev, {
        timestamp: new Date().toISOString(),
        level: 'info',
        message: 'Rollback completed successfully. Instance restored to original configuration.'
      }]);
      
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsDeploying(false);
    }
  };

  const getStepIcon = (status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped') => {
    switch (status) {
      case 'pending': return faClock;
      case 'running': return faSpinner;
      case 'completed': return faCheckCircle;
      case 'failed': return faExclamationTriangle;
      case 'skipped': return faTimes;
    }
  };

  const getStepColor = (status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped') => {
    switch (status) {
      case 'pending': return 'text-gray-500';
      case 'running': return 'text-blue-500';
      case 'completed': return 'text-green-500';
      case 'failed': return 'text-red-500';
      case 'skipped': return 'text-yellow-500';
    }
  };

  const getLogLevelColor = (level: 'info' | 'warning' | 'error') => {
    switch (level) {
      case 'info': return 'text-blue-600 bg-blue-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      case 'error': return 'text-red-600 bg-red-50';
    }
  };

  const getCloudIcon = (cloud: 'aws' | 'gcp' | 'github') => {
    switch (cloud) {
      case 'aws': return faAws;
      case 'gcp': return faGoogle;
      case 'github': return faGithub;
    }
  };

  const allStepsCompleted = steps.every(step => step.status === 'completed');
  const hasFailedSteps = steps.some(step => step.status === 'failed');
  const canRollback = deploymentStarted && (allStepsCompleted || hasFailedSteps) && !isDeploying;

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-mono">
      <div className="bg-white rounded shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Loading State */}
        {loading && (
          <div className="p-8 text-center">
            <FontAwesomeIcon icon={faSpinner} spin className="text-blue-600 text-2xl mb-3" />
            <div className="text-sm font-medium text-gray-900">Loading recommendation details...</div>
          </div>
        )}

        {/* Error State - Failed to Load */}
        {!loading && error && !recommendation && (
          <div className="p-4">
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-lg" />
                <div>
                  <div className="text-sm font-bold text-red-900">Failed to Load Recommendation</div>
                  <div className="text-xs text-red-700 mt-1">{error}</div>
                </div>
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={onClose}
                className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-100 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        {!loading && recommendation && (
          <>
            {/* Header */}
            <div className="sticky top-0 bg-blue-600 px-4 py-3 flex items-center justify-between border-b rounded-t">
              <div className="text-white">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={getCloudIcon(recommendation.cloud)}
                    className="text-base"
                  />
                  <h2 className="text-base font-bold">Deploy Recommendation</h2>
                </div>
                <p className="text-xs text-blue-100 mt-0.5">{recommendation.title}</p>
              </div>

              <button
                onClick={onClose}
                className="text-white hover:text-blue-200"
                disabled={isDeploying}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

        <div className="flex h-96">
          {/* Left Panel - Recommendation Details */}
          <div className="w-1/2 p-3 border-r border-gray-300 overflow-y-auto">
            <div className="space-y-3">
              {/* Impact */}
              <div className="bg-green-50 border border-green-300 rounded p-3">
                <div className="flex items-center gap-2 mb-2">
                  <FontAwesomeIcon icon={faDollarSign} className="text-green-600" />
                  <h3 className="text-sm font-bold text-green-900">Expected Savings</h3>
                </div>
                <div className="text-base font-bold text-green-700">
                  ${recommendation.impact.monthlySavings.toLocaleString()}/month
                </div>
                <div className="text-xs text-green-600 font-medium">
                  ${recommendation.impact.annualSavings.toLocaleString()}/year potential savings
                </div>
              </div>

              {/* Resource Details */}
              <div className="border border-gray-300 rounded p-3">
                <div className="flex items-center gap-2 mb-2">
                  <FontAwesomeIcon icon={faServer} className="text-gray-600" />
                  <h3 className="text-sm font-bold text-gray-900">Resource Details</h3>
                </div>
                <div className="space-y-1 text-xs">
                  <div>
                    <span className="text-gray-600">Name:</span> <span className="font-bold">{recommendation.resourceDetails.resourceName}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Type:</span> <span className="font-bold">{recommendation.resourceDetails.resourceType}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Region:</span> <span className="font-bold">{recommendation.resourceDetails.region}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Current:</span> <span className="font-bold">{JSON.stringify(recommendation.resourceDetails.currentConfig)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Recommended:</span> <span className="font-bold">{JSON.stringify(recommendation.resourceDetails.recommendedConfig)}</span>
                  </div>
                </div>
              </div>

              {/* Risk Assessment */}
              <div className="bg-yellow-50 border border-yellow-300 rounded p-3">
                <div className="flex items-center gap-2 mb-2">
                  <FontAwesomeIcon icon={faShieldAlt} className="text-yellow-600" />
                  <h3 className="text-sm font-bold text-yellow-900">Risk Assessment</h3>
                </div>
                <div className="text-xs text-yellow-800">
                  <div className="mb-1"><span className="font-bold">Effort:</span> {recommendation.effort}</div>
                  <div><span className="font-bold">Rollback:</span> Available if needed</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Deployment Progress */}
          <div className="w-1/2 p-3 overflow-y-auto">
            <div className="space-y-3">
              {/* Deployment Steps */}
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">Deployment Steps</h3>
                <div className="space-y-2">
                  {steps.map((step, index) => (
                    <div key={step.id} className="flex items-center gap-2">
                      <div className="flex-shrink-0">
                        <FontAwesomeIcon
                          icon={getStepIcon(step.status)}
                          className={`${getStepColor(step.status)} ${step.status === 'running' ? 'animate-spin' : ''}`}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="text-xs font-bold text-gray-900">{step.name}</div>
                        <div className="text-xs text-gray-600 font-medium">
                          Est. {step.duration}
                          {step.error && (
                            <span className="text-red-600 ml-2 font-bold">{step.error}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 font-medium">
                        Step {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Deployment Logs */}
              {logs.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-2">Deployment Logs</h3>
                  <div className="bg-gray-900 rounded p-2 max-h-48 overflow-y-auto text-xs">
                    {logs.map((log, index) => (
                      <div key={index} className={`mb-1 p-1 rounded ${getLogLevelColor(log.level)}`}>
                        <span className="text-gray-500 font-medium">
                          [{new Date(log.timestamp).toLocaleTimeString()}]
                        </span>{' '}
                        <span className="font-bold">{log.level.toUpperCase()}:</span>{' '}
                        {log.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-300 rounded p-3">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
                    <span className="text-sm font-bold text-red-900">Deployment Failed</span>
                  </div>
                  <div className="text-xs text-red-700 mt-1 font-medium">{error}</div>
                </div>
              )}
            </div>
          </div>
        </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 px-4 py-3 border-t flex justify-between items-center rounded-b">
              <div className="flex items-center gap-2 text-xs text-gray-600 font-medium">
                {deploymentStarted && (
                  <>
                    <FontAwesomeIcon icon={faFileText} />
                    <span>Deployment logs will be saved for audit purposes</span>
                  </>
                )}
              </div>

              <div className="flex items-center gap-2">
                {canRollback && (
                  <button
                    onClick={handleRollback}
                    disabled={isDeploying}
                    className="px-3 py-1.5 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 font-bold"
                  >
                    <FontAwesomeIcon icon={faUndo} className="mr-1" />
                    Rollback
                  </button>
                )}

                {!deploymentStarted && (
                  <button
                    onClick={handleStartDeployment}
                    disabled={isDeploying || steps.length === 0}
                    className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 font-bold"
                  >
                    <FontAwesomeIcon icon={faPlay} className="mr-1" />
                    Start Deployment
                  </button>
                )}

                <button
                  onClick={onClose}
                  disabled={isDeploying}
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 font-medium"
                >
                  {allStepsCompleted ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Step Confirmation Dialog */}
        {confirmationStep && confirmationCallback && (
          <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center z-10">
            <div className="bg-white rounded shadow-2xl max-w-2xl w-full mx-4 border border-blue-600">
              {/* Dialog Header */}
              <div className="bg-blue-600 px-4 py-3 rounded-t">
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-300" />
                  Confirm Deployment Step
                </h3>
              </div>

              {/* Dialog Body */}
              <div className="p-4 space-y-3">
                {/* Step Information */}
                <div className="bg-blue-50 border border-blue-300 rounded p-3">
                  <div className="text-xs font-bold text-blue-900 mb-1">Step Action:</div>
                  <div className="text-sm text-gray-900 font-bold">{confirmationStep.name}</div>
                </div>

                {/* Risk & Duration Info */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="border border-gray-300 rounded p-2">
                    <div className="text-xs text-gray-600 font-medium mb-1">Estimated Time</div>
                    <div className="text-sm font-bold text-gray-900">{confirmationStep.duration}</div>
                  </div>
                  <div className={`rounded p-2 border ${
                    confirmationStep.riskLevel === 'high' ? 'bg-red-50 border-red-300' :
                    confirmationStep.riskLevel === 'medium' ? 'bg-yellow-50 border-yellow-300' :
                    'bg-green-50 border-green-300'
                  }`}>
                    <div className="text-xs text-gray-600 font-medium mb-1">Risk Level</div>
                    <div className={`text-sm font-bold ${
                      confirmationStep.riskLevel === 'high' ? 'text-red-700' :
                      confirmationStep.riskLevel === 'medium' ? 'text-yellow-700' :
                      'text-green-700'
                    }`}>
                      {confirmationStep.riskLevel?.toUpperCase() || 'LOW'}
                    </div>
                  </div>
                  <div className="border border-gray-300 rounded p-2">
                    <div className="text-xs text-gray-600 font-medium mb-1">Rollback</div>
                    <div className="text-sm font-bold text-gray-900">
                      {confirmationStep.rollbackPossible ? '✓ Available' : '✗ Not Available'}
                    </div>
                  </div>
                </div>

                {/* Warning Message */}
                <div className="bg-yellow-50 border border-yellow-300 rounded p-3">
                  <div className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 mt-0.5" />
                    <div className="text-xs text-yellow-800">
                      <div className="font-bold mb-1">Before you proceed:</div>
                      <ul className="list-disc list-inside space-y-0.5 font-medium">
                        <li>Ensure you have tested this change in a non-production environment</li>
                        <li>Verify you have appropriate backups if needed</li>
                        <li>Confirm you understand the impact of this action</li>
                        {!confirmationStep.rollbackPossible && (
                          <li className="font-bold text-red-700">⚠️ This action cannot be rolled back automatically</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dialog Footer */}
              <div className="bg-gray-50 px-4 py-3 rounded-b flex items-center justify-between border-t border-gray-300">
                <button
                  onClick={confirmationCallback.onCancel}
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-100 font-medium"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-1" />
                  Cancel Deployment
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={confirmationCallback.onSkip}
                    className="px-3 py-1.5 text-xs bg-yellow-100 text-yellow-700 border border-yellow-300 rounded hover:bg-yellow-200 font-medium"
                  >
                    Skip This Step
                  </button>
                  <button
                    onClick={confirmationCallback.onConfirm}
                    className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 font-bold"
                  >
                    <FontAwesomeIcon icon={faPlay} className="mr-1" />
                    Confirm & Execute
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};